import React, { useEffect } from "react";
import { setTwoToneColor } from "@ant-design/icons";
import { useTenantTheme } from "../hooks/tenant";
import { useTenantThemeContext } from "../contexts/color-mode";
import { useColorMode } from "../contexts/color-mode";
import { getThemeConfig } from "../config/theme";

/**
 * Component that loads tenant theme settings
 * Must be rendered inside Refine component to have access to QueryClient
 */
export const TenantThemeLoader: React.FC = () => {
    useTenantTheme();
    const { tenantTheme } = useTenantThemeContext();
    const { mode } = useColorMode();

    // Set CSS variables for primary button styling; sync two-tone icons with resolved primary.
    // Use the same effective theme as ConfigProvider (getThemeConfig) so deployment merges
    // (e.g. deployment-theme.dark.json) apply when tenant theme is absent or partial.
    useEffect(() => {
        const root = document.documentElement;

        const colorMode = mode === "dark" ? "dark" : "light";
        const effectiveTheme = getThemeConfig(colorMode, tenantTheme);

        const buttonConfig = effectiveTheme.components?.Button as
            | { primaryButtonAccentColor?: string }
            | undefined;

        let accentColor = "#f37233";
        if (buttonConfig?.primaryButtonAccentColor) {
            accentColor = buttonConfig.primaryButtonAccentColor;
        } else if (effectiveTheme.token?.colorPrimary) {
            accentColor = String(effectiveTheme.token.colorPrimary);
        }

        setTwoToneColor(accentColor);
        
        // Helper function to parse hex color
        const parseHexColor = (hex: string) => {
            // Remove # if present
            const cleanHex = hex.replace("#", "");
            // Handle 3-digit hex
            if (cleanHex.length === 3) {
                return {
                    r: parseInt(cleanHex[0] + cleanHex[0], 16),
                    g: parseInt(cleanHex[1] + cleanHex[1], 16),
                    b: parseInt(cleanHex[2] + cleanHex[2], 16),
                };
            }
            // Handle 6-digit hex
            return {
                r: parseInt(cleanHex.slice(0, 2), 16),
                g: parseInt(cleanHex.slice(2, 4), 16),
                b: parseInt(cleanHex.slice(4, 6), 16),
            };
        };
        
        // Set CSS variable for accent color
        root.style.setProperty("--primary-button-accent-color", accentColor);
        
        // Create a darker, desaturated version for the semi-transparent background
        // This creates a dark golden-brown tone that works well with the accent color
        const accentRgb = parseHexColor(accentColor);
        
        // Create a darker, desaturated version (reduce brightness and saturation)
        // This mimics the dark golden-brown appearance from the design
        const darkerR = Math.max(0, Math.floor(accentRgb.r * 0.55));
        const darkerG = Math.max(0, Math.floor(accentRgb.g * 0.4));
        const darkerB = Math.max(0, Math.floor(accentRgb.b * 0.05));
        
        // Set background colors with different opacity levels
        const bgColor = `rgba(${darkerR}, ${darkerG}, ${darkerB}, 0.1)`;
        root.style.setProperty("--primary-button-bg-color", bgColor);
        
        // Hover state background (slightly more opaque)
        const hoverBgColor = `rgba(${darkerR}, ${darkerG}, ${darkerB}, 0.4)`;
        root.style.setProperty("--primary-button-bg-color-hover", hoverBgColor);
        
        // Active state background (more opaque)
        const activeBgColor = `rgba(${darkerR}, ${darkerG}, ${darkerB}, 0.5)`;
        root.style.setProperty("--primary-button-bg-color-active", activeBgColor);
        
    }, [tenantTheme, mode]);

    return null;
};
